import { LoiTermFormFieldKeys } from './loi-term-form-field-keys';
import { GeneralDeferredPaymentsFieldKeys } from './loi-term-form-field-keys';

export enum LoiTermFormTab {
    General = 1,
    PurchasePriceConsideration = 2,
    OwnerEmployment = 3,
    Property = 4,
    SellerRestrictiveCovenants = 5,
    SellerContractualObligations = 6,
}

export interface LoiTermFormTabDefinition {
    id: LoiTermFormTab;
    label: string;
}

export const LoiTermFormTabs: LoiTermFormTabDefinition[] = [
    { id: LoiTermFormTab.General, label: 'General' },
    { id: LoiTermFormTab.OwnerEmployment, label: 'Owner Employment' },
    { id: LoiTermFormTab.PurchasePriceConsideration, label: 'Purchase Price Consideration' },
    { id: LoiTermFormTab.Property, label: 'Property' },
    { id: LoiTermFormTab.SellerRestrictiveCovenants, label: 'Seller Restrictive Covenants' },
    { id: LoiTermFormTab.SellerContractualObligations, label: 'Seller Contractual Obligations' },
];

export const LOI_TERM_FORM_TAB_SEARCH_PARAM = 'loitab';

export const LOI_FILE_REQUIRED_FIELD_KEY = 'loiFileRequired';

const GENERAL_FIELD_KEYS: string[] = [
    LOI_FILE_REQUIRED_FIELD_KEY,
    LoiTermFormFieldKeys.loiExpirationDate,
    LoiTermFormFieldKeys.exclusivityPeriodDays,
    LoiTermFormFieldKeys.practicePropertyPurchased,
    'isLegalInfoConfirmed',
];

const PURCHASE_PRICE_CONSIDERATION_FIELD_KEYS: string[] = [
    LoiTermFormFieldKeys.cashAtClose,
    'workingCapitalHoldbackValue',
    'workingCapitalHoldbackMonths',
    'deferredFixedPayments',
    'earnoutValueAtClosing',
    'syntheticJvEquityValueAtClosing',
    'jvEquityValueAtClosing',
    'corporateEquityCommonValueAtClosing',
    'corporateEquityPreferredValueAtClosing',
    LoiTermFormFieldKeys.other,
    ...GeneralDeferredPaymentsFieldKeys,
    'cashAtCloserConsiderationForEmployees',
    'workingCapitalHoldbackConsiderationForEmployees',
    'deferredFixedPaymentsConsiderationForEmployees',
    'earnoutConsiderationForEmployees',
    'syntheticJvEquityConsiderationForEmployees',
    'jvEquityConsiderationForEmployees',
    'corporateEquityPreferredConsiderationForEmployees',
    'corporateEquityCommonConsiderationForEmployees',
    'otherConsiderationForEmployees',
    'cashAtCloserBuyerConsiderationForEmployees',
    'workingCapitalHoldbackBuyerConsiderationForEmployees',
    'deferredFixedPaymentsBuyerConsiderationForEmployees',
    'earnoutBuyerConsiderationForEmployees',
    'syntheticJvEquityBuyerConsiderationForEmployees',
    'jvEquityBuyerConsiderationForEmployees',
    'corporateEquityPreferredBuyerConsiderationForEmployees',
    'corporateEquityCommonBuyerConsiderationForEmployees',
    'otherBuyerConsiderationForEmployees',
    'cashAtCloserFairValue',
    'workingCapitalHoldbackFairValue',
    'deferredFixedPaymentsFairValue',
    'earnoutFairValue',
    'syntheticJvEquityFairValue',
    'jvEquityFairValue',
    'corporateEquityPreferredFairValue',
    'corporateEquityCommonFairValue',
    'otherFairValue',
];

const OWNER_EMPLOYMENT_FIELD_KEYS: string[] = [
    'sellingOwnerEmploymentAgreementRows',
    'employeeConsiderationPayer',
];

const PROPERTY_FIELD_KEYS: string[] = [
    'locations',
];

const PURCHASE_PRICE_CONSIDERATION_ROW_KEY_PREFIXES: string[] = [
    'dfp:',
    'dvp:',
    'jvEquity:',
    'corporateEquity:Common:',
    'corporateEquity:Preferred:',
];

const PROPERTY_ROW_KEY_PREFIXES: string[] = [
    'lease:',
];

const SELLER_RESTRICTIVE_COVENANTS_ROW_KEY_PREFIXES: string[] = [
    'sellerCovenant:',
];

const FIELD_KEY_TO_TAB = new Map<string, LoiTermFormTab>([
    ...GENERAL_FIELD_KEYS.map((key): [string, LoiTermFormTab] => [key, LoiTermFormTab.General]),
    ...PURCHASE_PRICE_CONSIDERATION_FIELD_KEYS.map((key): [string, LoiTermFormTab] => [key, LoiTermFormTab.PurchasePriceConsideration]),
    ...OWNER_EMPLOYMENT_FIELD_KEYS.map((key): [string, LoiTermFormTab] => [key, LoiTermFormTab.OwnerEmployment]),
    ...PROPERTY_FIELD_KEYS.map((key): [string, LoiTermFormTab] => [key, LoiTermFormTab.Property]),
    ['vendorContractRows', LoiTermFormTab.SellerContractualObligations],
]);

const ROW_KEY_PREFIX_TO_TAB: Array<[string, LoiTermFormTab]> = [
    ...PURCHASE_PRICE_CONSIDERATION_ROW_KEY_PREFIXES.map((prefix): [string, LoiTermFormTab] => [prefix, LoiTermFormTab.PurchasePriceConsideration]),
    ...PROPERTY_ROW_KEY_PREFIXES.map((prefix): [string, LoiTermFormTab] => [prefix, LoiTermFormTab.Property]),
    ...SELLER_RESTRICTIVE_COVENANTS_ROW_KEY_PREFIXES.map((prefix): [string, LoiTermFormTab] => [prefix, LoiTermFormTab.SellerRestrictiveCovenants]),
];

export function getTabForTermFormFieldKey(fieldKey: string): LoiTermFormTab {
    const exactMatch = FIELD_KEY_TO_TAB.get(fieldKey);
    if (exactMatch !== undefined) {
        return exactMatch;
    }
    const prefixMatch = ROW_KEY_PREFIX_TO_TAB.find(([prefix]) => fieldKey.startsWith(prefix));
    if (prefixMatch) {
        return prefixMatch[1];
    }
    return LoiTermFormTab.General;
}
